import { graphql, useStaticQuery } from "gatsby"
import React, { useContext } from "react"
import { css } from "styled-components"
import Spacer from "../../design-system/atoms/layout-primitives/spacer"
import { T } from "../../design-system/atoms/text"
import VariantContainer from "../../design-system/atoms/variant-container"
import { IPublicURLQuery } from "../../design-system/types/imageQuery.types"
import withAnimation, {
  FadeIn,
  FadeInBottom,
} from "../../design-system/util/withAnimation"
import Breakpoints from "../shared/breakpoints"
import FadedTitle from "../shared/faded-title"
import Grid from "../shared/grid"
import { UIContext } from "../shared/layout"
import Link from "../shared/link"
import SectionDivider from "../shared/section-divider"
import {
  OutlineLinkStyle,
  StatItem,
  StatItemDescription,
  StatItemNumber,
} from "./sdk-overview.style"

const AnimatedVariantContainer = withAnimation(VariantContainer)

interface ISdkOverviewStaticQuery {
  desktopSdk: IPublicURLQuery
  mobileSdk: IPublicURLQuery
  sdkFade: IPublicURLQuery
}

export default function SdkOverview() {
  const { desktopSdk, mobileSdk, sdkFade } =
    useStaticQuery<ISdkOverviewStaticQuery>(graphql`
      query {
        desktopSdk: file(
          relativePath: { eq: "1_SOLUTIONS/Developer Tools/Desktop/sdks.svg" }
        ) {
          id
          publicURL
        }
        mobileSdk: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/Mobile/sdks_mobile.svg"
          }
        ) {
          id
          publicURL
        }
        sdkFade: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/Desktop/fade_titles/sdks_fade.svg"
          }
        ) {
          id
          publicURL
        }
      }
    `)

  const {
    state: { breakpoint },
  } = useContext(UIContext)

  return (
    <>
      <SectionDivider />
      <Spacer
        height={{ sm: "64px", md: "48px", lg: "160px" }}
        variants={{
          sm: css`
            background-color: var(--tricorn-black);
          `,
        }}
      />
      <Grid
        overlayStyle={css`
          background-color: var(--tricorn-black);
        `}
      >
        <AnimatedVariantContainer
          animation={FadeIn}
          variants={[
            css`
              grid-column: 1/-1;
              justify-self: center;
              & > img {
                max-width: 302.11px;
              }
            `,
            css`
              grid-row: 1 / 3;
              grid-column: 1 / 6;
              align-self: center;
              width: 100%;
              & > img {
                justify-self: right;
                width: 100%;
                max-width: 293.19px;
              }
            `,
            css`
              & > img {
                width: 100%;
                max-width: 508.91px;
              }
              @media (min-width: ${Breakpoints.lgPlus}) {
                grid-column: 2 / 7;
              }
            `,
          ]}
        >
          <img
            src={
              breakpoint === "sm" ? mobileSdk.publicURL : desktopSdk.publicURL //desktop and tablet use the same image
            }
            style={{ objectFit: "contain" }}
          />
        </AnimatedVariantContainer>
        <AnimatedVariantContainer
          animation={FadeIn}
          variants={[
            css`
              padding-bottom: 32px;
              grid-row: 1;
              grid-column: 1 / -1;
              text-align: center;

              & h3 {
                color: var(--white);
              }
            `,
            css`
              text-align: left;
              grid-column: -7 / -1;
              padding: 0;
            `,
            css`
              @media (min-width: ${Breakpoints.lgPlus}) {
                grid-column: -6 / -1;
              }
            `,
          ]}
        >
          <FadedTitle
            fadeSettings={{ lg: "left" }}
            image={sdkFade.publicURL}
            title="Mappedin SDKs"
            alt='The word "exports" displayed faded behind the title'
          />
        </AnimatedVariantContainer>
        <VariantContainer
          variants={[
            css`
              grid-column: 2 / -2;
              color: var(--white);
              display: flex;
              flex-direction: column;
            `,
            css`
              grid-column: -7 / -1;
            `,
            css`
              @media (min-width: ${Breakpoints.lgPlus}) {
                grid-column: -6 / -1;
              }
            `,
          ]}
        >
          <AnimatedVariantContainer
            animation={FadeInBottom}
            variants={[
              css`
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                border-top: 1px solid rgba(255, 255, 255, 0.25);
                margin-top: 32px;
                padding: 16px 0 0 0;
                & > ul:first-child {
                  grid-column: 1/2;
                  padding-right: 32px;
                }
                & > ul:last-child {
                  grid-column: 2/-1;
                }
              `,
              css`
                margin-top: 16px;
              `,
            ]}
          >
            <StatItem>
              <StatItemNumber>500</StatItemNumber>
              <StatItemDescription>Implementations</StatItemDescription>
            </StatItem>
            <StatItem>
              <StatItemNumber>
                26.5<span>M</span>
              </StatItemNumber>
              <StatItemDescription>SDK Users</StatItemDescription>
            </StatItem>
          </AnimatedVariantContainer>
          <AnimatedVariantContainer
            animation={FadeInBottom}
            variants={[
              css`
                ${T.body.small`300`};
                padding: 32px 0 48px 0;
              `,
              css`
                ${T.md.body`300`};
                padding: 32px 0;
              `,
              css`
                ${T.lg.body`300`};
                padding: 32px 0 48px 0;
              `,
            ]}
          >
            Mappedin’s SDKs provide the tools required to render your digital
            map in 3D and build a completely custom mapping experience. With
            access to getting started guides, sample applications, and our
            professional developer network, we can help bring your mapping
            vision to life.
          </AnimatedVariantContainer>
          <AnimatedVariantContainer
            animation={FadeInBottom}
            variants={[
              css`
                max-width: 282px;
                display: flex;
                flex-wrap: wrap;
                align-self: center;
                & > *:not(:last-child) {
                  margin-bottom: 16px;
                }
              `,
              css`
                align-self: flex-start;
              `,
            ]}
          >
            <Link
              styleModifier={{ flex: "1 1 100%" }}
              href="https://developer.mappedin.com"
            >
              Start Building
            </Link>
            <Link
              href="https://www.mappedin.com/mapping/sdks/"
              styleModifier={OutlineLinkStyle}
            >
              Learn More
            </Link>
          </AnimatedVariantContainer>
        </VariantContainer>
      </Grid>
      <Spacer
        height={{ sm: "64px", md: "48px", lg: "96px" }}
        variants={{
          sm: css`
            background-color: var(--tricorn-black);
          `,
        }}
      />
      <SectionDivider flip />
    </>
  )
}
